import { describeGeometry } from '../../../data/import/geojsonInspection';
import { isTableSource, type VectorSourceDescriptor } from '../../../data/model';
import type { LayerDefinition } from '../../../project/model';
import { useAppStore } from '../../state/useAppStore';
import { DisplayFilterEditor } from './DisplayFilterEditor';
import { JoinsEditor } from './JoinsEditor';
import { effectiveVectorSourceForLayer } from '../../../joins/effectiveLayer';

interface DataInspectorProps {
  readonly layer: LayerDefinition;
  readonly source: VectorSourceDescriptor;
}

function sourceFormat(source: VectorSourceDescriptor): string {
  switch (source.provenance.originalFormat) {
    case 'csv':
      return 'CSV coordinates';
    case 'xlsx':
      return 'XLSX coordinates';
    case 'shapefile':
      return 'Shapefile ZIP';
    case 'geojson':
      return 'GeoJSON';
    case 'kml':
      return 'KML';
    case 'kmz':
      return 'KMZ';
    case 'gpkg':
      return 'GeoPackage';
  }
}

export function DataInspector({ layer, source }: DataInspectorProps) {
  const project = useAppStore((state) => state.project);
  const tableOpen = useAppStore((state) => state.shell.attributeTableOpen);
  const openAttributeTable = useAppStore((state) => state.openAttributeTable);
  const closeAttributeTable = useAppStore((state) => state.closeAttributeTable);
  const effectiveSource = effectiveVectorSourceForLayer(project.sources, layer) ?? source;
  const hasFields = effectiveSource.fields.length > 0;
  const joinedFieldCount = effectiveSource.fields.length - source.fields.length;
  const tables = project.sources.filter(isTableSource);

  return (
    <div className="inspector-content inspector-data">
      <section className="inspector-section data-table-action">
        <button
          className={tableOpen ? '' : 'primary-action'}
          disabled={!hasFields && !tableOpen}
          onClick={tableOpen ? closeAttributeTable : openAttributeTable}
          title={!hasFields && !tableOpen ? 'This source has no attribute fields.' : undefined}
          type="button"
        >
          {tableOpen ? 'Close attribute table' : 'Open attribute table'}
        </button>
        {!hasFields ? (
          <p className="empty-value">This source has no attribute fields to inspect.</p>
        ) : null}
      </section>

      <DisplayFilterEditor
        fields={effectiveSource.fields}
        filter={layer.displayFilter}
        layerId={layer.id}
      />

      <JoinsEditor
        key={tables.map((table) => table.id).join(':')}
        layer={layer}
        source={source}
        tables={tables}
      />

      <section className="inspector-section">
        <h3>Data</h3>
        <dl className="metadata-list metadata-list-wide">
          <div>
            <dt>Layer</dt>
            <dd title={layer.name}>{layer.name}</dd>
          </div>
          <div>
            <dt>Source</dt>
            <dd title={source.name}>{source.name}</dd>
          </div>
          <div>
            <dt>Features</dt>
            <dd>{source.featureCount.toLocaleString()}</dd>
          </div>
          <div>
            <dt>Source fields</dt>
            <dd>{source.fields.length.toLocaleString()}</dd>
          </div>
          {joinedFieldCount > 0 ? (
            <div>
              <dt>Joined fields</dt>
              <dd>{joinedFieldCount.toLocaleString()}</dd>
            </div>
          ) : null}
          <div>
            <dt>Geometry</dt>
            <dd>{describeGeometry(source.geometry)}</dd>
          </div>
        </dl>
      </section>

      <section className="inspector-section">
        <h3>Source</h3>
        <dl className="metadata-list metadata-list-wide">
          <div>
            <dt>Format</dt>
            <dd>{sourceFormat(source)}</dd>
          </div>
          <div>
            <dt>Original file</dt>
            <dd title={source.provenance.originalFileName}>{source.provenance.originalFileName}</dd>
          </div>
          {source.provenance.datasetName ? (
            <div>
              <dt>Dataset</dt>
              <dd title={source.provenance.datasetName}>{source.provenance.datasetName}</dd>
            </div>
          ) : null}
          <div>
            <dt>Original CRS</dt>
            <dd>{source.crs.identifier ?? source.crs.name ?? 'Unknown'}</dd>
          </div>
          <div>
            <dt>Canonical CRS</dt>
            <dd>{source.crs.canonicalIdentifier}</dd>
          </div>
          {source.crs.transformation === 'reprojected' ? (
            <div>
              <dt>Coordinates</dt>
              <dd>Reprojected on import</dd>
            </div>
          ) : null}
        </dl>
      </section>

      {source.warnings.length > 0 ? (
        <section className="inspector-section">
          <h3>
            Warnings <span className="section-count">{source.warnings.length}</span>
          </h3>
          {source.warnings.map((warning) => (
            <p className="layer-warning" key={warning.code} role="status">
              {warning.message}
            </p>
          ))}
        </section>
      ) : null}
    </div>
  );
}
